import { memo } from 'react'

import { Skeleton, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { workspaceSettingsPermissionsInvitationsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Settings'
import { environmentQueryKeys, organizationQueryKeys, workspaceQueryKeys } from 'services/queryKeys'
import { resolveDisplayName } from 'utils/userInfo'

type ResolvedUserProps = {
  userId: string
}

const ResolvedUserRaw = ({ userId: memberId }: ResolvedUserProps) => {
  const { t } = useTranslation()

  const { environmentId, workspaceId } = workspaceSettingsPermissionsInvitationsRoute.useParams()

  const workspaceMemberUserQuery = useQuery({
    ...workspaceQueryKeys.memberDetail({ workspaceId, memberId }),
    meta: {
      optOutOfGlobalErrorHandling: true,
    },
  })
  const user = workspaceMemberUserQuery.data

  const environmentUserQuery = useQuery({
    ...environmentQueryKeys.memberDetail({ environmentId, memberId }),
    meta: {
      optOutOfGlobalErrorHandling: true,
    },
    enabled: workspaceMemberUserQuery.isError,
  })
  const environmentUser = environmentUserQuery.data

  const environmentQuery = useQuery({
    ...environmentQueryKeys.detail({ environmentId }),
    enabled: environmentUserQuery.isError,
  })

  const organizationUserQuery = useQuery({
    ...organizationQueryKeys.memberDetail({
      organizationId: environmentQuery.data?.organizationId as string,
      memberId,
    }),
    enabled: environmentUserQuery.isError && environmentQuery.isSuccess,
  })
  const organizationUser = organizationUserQuery.data

  const isLoadingAggregated =
    workspaceMemberUserQuery.isLoading ||
    environmentUserQuery.isLoading ||
    environmentQuery.isLoading ||
    organizationUserQuery.isLoading

  if (isLoadingAggregated) {
    return (
      <Typography sx={{ width: '100%' }} variant="inherit">
        <Skeleton sx={{ width: '100%' }} />
      </Typography>
    )
  }

  const derivedUserInfoAggregated =
    resolveDisplayName(user?.userInfo) ??
    resolveDisplayName(environmentUser?.userInfo) ??
    resolveDisplayName(organizationUser?.userInfo)

  return (
    <Typography variant="inherit">
      {!derivedUserInfoAggregated ? t('resourceNotFound') : derivedUserInfoAggregated}
    </Typography>
  )
}

export const ResolvedUser = memo(ResolvedUserRaw)
